/**
 * Notification level per channel and per server, as this device sees it.
 *
 * Effective level for a channel — shared `resolveMuteLevel`, the same rule the
 * server's push gate uses:
 *   1. the channel's own setting (an explicit ALL included: it beats a muted
 *      server),
 *   2. its server's setting,
 *   3. ALL.
 * A timed mute that has run out counts as no setting.
 *
 * The local copy (localStorage) answers instantly and never waits on the
 * network. The server copy is the shared truth — it gates push and follows the
 * user across devices — so it replaces the local one on boot
 * ({@link syncMutesFromServer}) and on every `mute:update`
 * ({@link applyMuteUpdate}), except for changes this device has not delivered
 * yet.
 *
 * Levels:
 *   ALL         — sound + banner + unread counter (default)
 *   ALL_SILENT  — banner + counter, but NO sound ping
 *   MENTIONS    — only when @mentioned or replied to (still pings sound)
 *   NOTHING     — fully silent, counters suppressed too
 */
import { resolveMuteLevel, type MuteLevel } from '@bunker/shared/mutes';
import type { MuteUpdatePayload } from '@bunker/shared/socketEvents';
import i18n from '@/i18n';
// Shared API client. This is acyclic today (api.ts only depends on
// @bunker/shared/api). Keep it that way: api.ts must not import any client
// module that transitively imports muteSettings, or this edge becomes a cycle.
import { api } from '@/lib/api';

export type NotificationLevel = MuteLevel;

const STORAGE_KEY = 'bunker.muteSettings.v1';

interface MuteSettings {
  channels: Record<string, NotificationLevel>;
  /** Epoch-ms when a timed channel setting expires. */
  channelMutedUntil: Record<string, number>;
  guilds: Record<string, NotificationLevel>;
  /** Epoch-ms when a timed server mute expires. */
  guildMutedUntil: Record<string, number>;
  /**
   * The server used to delete a channel's ALL row, so an explicit "everything"
   * inside a muted server lived only here. Set once those have been sent to
   * the server, which now keeps them.
   */
  explicitAllSynced?: boolean;
}

function empty(): MuteSettings {
  return { channels: {}, channelMutedUntil: {}, guilds: {}, guildMutedUntil: {} };
}

function load(): MuteSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return empty();
    const parsed = JSON.parse(raw) as Partial<MuteSettings>;
    return {
      channels: parsed.channels ?? {},
      channelMutedUntil: parsed.channelMutedUntil ?? {},
      guilds: parsed.guilds ?? {},
      guildMutedUntil: parsed.guildMutedUntil ?? {},
      explicitAllSynced: parsed.explicitAllSynced,
    };
  } catch {
    return empty();
  }
}

function save(settings: MuteSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage can be unavailable in private mode — silently degrade
    // to in-memory only (the change still applies for this session).
  }
}

/**
 * Mute changes that haven't reached the server yet.
 *
 * A failed sync is remembered instead of dropped, and retried on the next boot
 * and whenever the browser says the network is back: otherwise the app looked
 * muted while the phone kept buzzing, and the next boot adopted the server's
 * older view, silently undoing the change. Keyed by target, so only the latest
 * intent per channel/guild survives: mute-then-unmute leaves one unmute.
 */
const PENDING_KEY = 'bunker.muteSync.pending.v1';

type PendingSync =
  | { kind: 'channel'; id: string; level: NotificationLevel }
  | { kind: 'guild'; id: string; level: NotificationLevel; mutedUntil: string | null };

type TargetKey = `${'channel' | 'guild'}:${string}`;

function targetKey(kind: 'channel' | 'guild', id: string): TargetKey {
  return `${kind}:${id}`;
}

function loadPending(): Record<string, PendingSync> {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as Record<string, PendingSync>;
  } catch {
    return {};
  }
}

function savePending(map: Record<string, PendingSync>): void {
  try {
    if (Object.keys(map).length === 0) localStorage.removeItem(PENDING_KEY);
    else localStorage.setItem(PENDING_KEY, JSON.stringify(map));
  } catch {
    // Private mode: retries just don't survive a reload. The local mute still
    // applies, which is the part the user can see.
  }
}

function rememberPending(p: PendingSync): void {
  const map = loadPending();
  map[targetKey(p.kind, p.id)] = p;
  savePending(map);
}

function forgetPending(p: PendingSync): void {
  const map = loadPending();
  const key = targetKey(p.kind, p.id);
  if (!(key in map)) return;
  delete map[key];
  savePending(map);
}

/** Number of mute changes still waiting to reach the server. For tests + UI. */
export function pendingMuteSyncCount(): number {
  return Object.keys(loadPending()).length;
}

/** Requests on their way right now, per target. */
const inFlight = new Map<TargetKey, number>();

/** A local change the server may not know about yet: queued or on its way. */
function isUndelivered(key: TargetKey): boolean {
  return key in loadPending() || (inFlight.get(key) ?? 0) > 0;
}

function pushPending(p: PendingSync): Promise<unknown> {
  if (p.kind === 'channel') return api.setChannelMute(p.id, p.level);
  // ALL is the server default: no row.
  if (p.level === 'ALL') return api.clearGuildMute(p.id);
  return api.setGuildMute(p.id, p.level, p.mutedUntil);
}

/** Send one intent, remembering it for later if the server can't be reached. */
async function trySync(p: PendingSync): Promise<void> {
  const key = targetKey(p.kind, p.id);
  inFlight.set(key, (inFlight.get(key) ?? 0) + 1);
  try {
    await pushPending(p);
    // A previous failure for this same target is now moot.
    forgetPending(p);
  } catch {
    rememberPending(p);
  } finally {
    const left = (inFlight.get(key) ?? 1) - 1;
    if (left > 0) inFlight.set(key, left);
    else inFlight.delete(key);
  }
}

/**
 * Retry everything that never made it. Resolves once every intent has been
 * tried; whatever still fails stays queued for the next attempt.
 */
export async function flushPendingMuteSync(): Promise<void> {
  const entries = Object.values(loadPending());
  if (entries.length === 0) return;
  await Promise.all(entries.map((p) => trySync(p)));
}

let onlineListenerBound = false;

/** Retry queued syncs when the browser reports the network is back. Idempotent. */
export function bindMuteSyncRetry(): void {
  if (onlineListenerBound || typeof window === 'undefined') return;
  onlineListenerBound = true;
  window.addEventListener('online', () => {
    void flushPendingMuteSync();
  });
}

const listeners = new Set<() => void>();
let cache: MuteSettings | null = null;

/** Local changes, numbered, so a server snapshot older than one is not adopted over it. */
let changeSeq = 0;
const changedAt = new Map<TargetKey, number>();

function getSettings(): MuteSettings {
  if (!cache) cache = load();
  return cache;
}

/** Evict the in-memory cache so the next read reloads from localStorage.
 * Only exported for unit tests — do not call from production code. */
export function _resetCacheForTest(): void {
  cache = null;
  inFlight.clear();
  changedAt.clear();
}

function commit(next: MuteSettings, changed?: TargetKey): void {
  cache = next;
  save(next);
  if (changed) changedAt.set(changed, ++changeSeq);
  for (const fn of listeners) fn();
}

export function subscribeMuteSettings(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function activeLevel(level: NotificationLevel | undefined, until: number | undefined): NotificationLevel | null {
  if (!level) return null;
  if (until !== undefined && Date.now() >= until) return null;
  return level;
}

/** Effective level for a channel given its parent guild (null for DMs, which
 *  have nothing to inherit). */
export function getChannelLevel(channelId: string, guildId: string | null): NotificationLevel {
  const s = getSettings();
  return resolveMuteLevel(
    activeLevel(s.channels[channelId], s.channelMutedUntil[channelId]),
    guildId ? activeLevel(s.guilds[guildId], s.guildMutedUntil[guildId]) : null,
  );
}

export function getGuildLevel(guildId: string): NotificationLevel {
  const s = getSettings();
  return resolveMuteLevel(null, activeLevel(s.guilds[guildId], s.guildMutedUntil[guildId]));
}

/** True if the guild is currently silenced (NOTHING, honouring timed expiry). */
export function isGuildMuted(guildId: string): boolean {
  return getGuildLevel(guildId) === 'NOTHING';
}

function without<T>(map: Record<string, T>, id: string): Record<string, T> {
  if (!(id in map)) return map;
  const { [id]: _gone, ...rest } = map;
  return rest;
}

/** Silence a whole guild. `durationMs = null` mutes until manually re-enabled;
 *  a number mutes for that long (Telegram/Discord "mute for N hours"). */
export function muteGuild(guildId: string, durationMs: number | null): void {
  const s = getSettings();
  const until = durationMs == null ? null : Date.now() + durationMs;
  commit(
    {
      ...s,
      guilds: { ...s.guilds, [guildId]: 'NOTHING' },
      guildMutedUntil: until == null ? without(s.guildMutedUntil, guildId) : { ...s.guildMutedUntil, [guildId]: until },
    },
    targetKey('guild', guildId),
  );
  // The original deadline travels with the intent: a retry an hour later must
  // not silently extend the mute by an hour.
  void trySync({
    kind: 'guild',
    id: guildId,
    level: 'NOTHING',
    mutedUntil: until == null ? null : new Date(until).toISOString(),
  });
}

/** Re-enable notifications for a guild (clears level + any timed expiry). */
export function unmuteGuild(guildId: string): void {
  const s = getSettings();
  commit(
    { ...s, guilds: without(s.guilds, guildId), guildMutedUntil: without(s.guildMutedUntil, guildId) },
    targetKey('guild', guildId),
  );
  void trySync({ kind: 'guild', id: guildId, level: 'ALL', mutedUntil: null });
}

/** Set a channel's own level. Always stored explicitly — picking ALL on a
 *  channel inside a muted guild must override the guild, not fall through. */
export function setChannelLevel(channelId: string, level: NotificationLevel): void {
  const s = getSettings();
  commit(
    {
      ...s,
      channels: { ...s.channels, [channelId]: level },
      channelMutedUntil: without(s.channelMutedUntil, channelId),
    },
    targetKey('channel', channelId),
  );
  void trySync({ kind: 'channel', id: channelId, level });
}

type ServerRow = { id: string; level: NotificationLevel; mutedUntil: string | null };

/**
 * One kind of rows from the server, adopted as the whole truth for that kind:
 * a target missing from them has no setting (unmuted on another device) —
 * unless this device changed it and the server may not know yet.
 */
function adoptRows(
  kind: 'channel' | 'guild',
  local: { levels: Record<string, NotificationLevel>; until: Record<string, number> },
  rows: readonly ServerRow[],
  keepLocal: (key: TargetKey) => boolean,
): { levels: Record<string, NotificationLevel>; until: Record<string, number> } {
  const levels: Record<string, NotificationLevel> = {};
  const until: Record<string, number> = {};
  for (const row of rows) {
    if (keepLocal(targetKey(kind, row.id))) continue;
    levels[row.id] = row.level;
    if (row.mutedUntil) until[row.id] = Date.parse(row.mutedUntil);
  }
  for (const id of Object.keys(local.levels)) {
    if (!keepLocal(targetKey(kind, id))) continue;
    levels[id] = local.levels[id]!;
    if (local.until[id] !== undefined) until[id] = local.until[id]!;
  }
  return { levels, until };
}

/**
 * Boot sync: deliver the changes that never made it, then adopt the server's
 * view. The order matters — adopting first would overwrite a local change that
 * simply hadn't been delivered. A request that fails leaves that kind as is.
 */
export async function syncMutesFromServer(): Promise<void> {
  await flushPendingMuteSync();
  const since = changeSeq;
  const [channelRes, guildRes] = await Promise.allSettled([api.listMyMutes(), api.listMyGuildMutes()]);
  const keepLocal = (key: TargetKey) => isUndelivered(key) || (changedAt.get(key) ?? 0) > since;
  const s = getSettings();
  let next = s;

  // A malformed answer is treated like a failed request: never adopt "no rows"
  // from something that is not a list of rows.
  if (channelRes.status === 'fulfilled' && Array.isArray(channelRes.value?.mutes)) {
    const rows = channelRes.value.mutes.map((row) => ({ ...row, id: row.channelId }));
    const adopted = adoptRows('channel', { levels: s.channels, until: s.channelMutedUntil }, rows, keepLocal);
    if (!s.explicitAllSynced) {
      // Once: an explicit ALL the old server deleted still lives only here.
      const onServer = new Set(rows.map((row) => row.id));
      for (const [id, level] of Object.entries(s.channels)) {
        if (level !== 'ALL' || onServer.has(id) || id in adopted.levels) continue;
        adopted.levels[id] = 'ALL';
        void trySync({ kind: 'channel', id, level: 'ALL' });
      }
    }
    next = { ...next, channels: adopted.levels, channelMutedUntil: adopted.until, explicitAllSynced: true };
  }
  if (guildRes.status === 'fulfilled' && Array.isArray(guildRes.value?.mutes)) {
    const rows = guildRes.value.mutes.map((row) => ({ ...row, id: row.guildId }));
    const adopted = adoptRows('guild', { levels: s.guilds, until: s.guildMutedUntil }, rows, keepLocal);
    next = { ...next, guilds: adopted.levels, guildMutedUntil: adopted.until };
  }
  if (next !== s) commit(next);
}

/**
 * A setting changed on another tab or device (or this one's echo). Ignored
 * while this device has its own newer change for the same target on the way.
 */
export function applyMuteUpdate(p: MuteUpdatePayload): void {
  if (isUndelivered(targetKey(p.kind, p.id))) return;
  const s = getSettings();
  const levels = p.kind === 'channel' ? s.channels : s.guilds;
  const untilMap = p.kind === 'channel' ? s.channelMutedUntil : s.guildMutedUntil;
  const until = p.mutedUntil ? Date.parse(p.mutedUntil) : undefined;
  if (levels[p.id] === (p.level ?? undefined) && untilMap[p.id] === until) return;
  const nextLevels = p.level ? { ...levels, [p.id]: p.level } : without(levels, p.id);
  const nextUntil = until !== undefined ? { ...untilMap, [p.id]: until } : without(untilMap, p.id);
  commit(
    p.kind === 'channel'
      ? { ...s, channels: nextLevels, channelMutedUntil: nextUntil }
      : { ...s, guilds: nextLevels, guildMutedUntil: nextUntil },
  );
}

/** Human label for a level — kept here so UI and notification gating
 * agree on names. */
export function levelLabel(level: NotificationLevel): string {
  switch (level) {
    case 'ALL':
      return i18n.t('lib2.mute.all');
    case 'ALL_SILENT':
      return i18n.t('lib2.mute.allSilent');
    case 'MENTIONS':
      return i18n.t('lib2.mute.mentions');
    case 'NOTHING':
      return i18n.t('lib2.mute.nothing');
  }
}

export function levelDescription(level: NotificationLevel): string {
  switch (level) {
    case 'ALL':
      return i18n.t('lib2.mute.allDesc');
    case 'ALL_SILENT':
      return i18n.t('lib2.mute.allSilentDesc');
    case 'MENTIONS':
      return i18n.t('lib2.mute.mentionsDesc');
    case 'NOTHING':
      return i18n.t('lib2.mute.nothingDesc');
  }
}
